import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formValueSelector, registerField } from 'redux-form';
import findIndex from 'lodash-es/findIndex';
import set from 'lodash-es/set';
import CalendarBlankOutline from 'mdi-material-ui/CalendarBlankOutline';
import Car from 'mdi-material-ui/Car';
import GasStation from 'mdi-material-ui/GasStation';
import Cog from 'mdi-material-ui/Cog';
import CarDoor from 'mdi-material-ui/CarDoor';
import Motorbike from 'mdi-material-ui/Motorbike';
import Engine from 'mdi-material-ui/Engine';
import Speedometer from 'mdi-material-ui/Speedometer';

import { selectIsRenewal, selectQuotation } from 'app/store/data/quotation';
import { useField } from './useField';
import { useActiveQuestion } from './useActiveQuestion';
import {
  getGroupedOptions,
  groupByOptionGroup,
} from './NewCarSelectControl.service';
import { StepSelectControlProps } from 'app/components/StepSelectControl/StepSelectControl.interface';
import {
  CompareCepikDataEnum,
  ControlLabelEnum,
  DictionariesEnum,
  FieldsEnum,
  FormsEnum,
  LabelEnum,
} from 'app/shared/enums';
import { CarMakeSelectOption } from 'app/components/CarMakeSelectOption';
import { CarModelDetailsSelectOption } from 'app/components/CarModelDetailsSelectOption';
import { getNewArrWithLastItemReplaced } from 'app/shared/helpers/getNewArrWithLastItemReplaced';
import {
  clearFieldValue,
  clearFieldsValue,
  clearVin,
} from 'app/services/businessFunctionHelpers';
import { isMotorInfoEkspertDisable } from 'app/services/env.service';
import { Dictionary, DictionaryEntry } from 'app/services/dict.service';
import { UtilService } from 'app/services/util.service';
import obfuscation from 'app/services/businessFunctions/calculation/car/obfuscation';
import { RootState } from 'app/store/rootReducer';
import {
  FormsConfigActions,
  selectIsMubiLeasingConfig,
} from 'app/store/data/formsConfig';
import { clearCepikMergeResult } from 'app/store/thunks/clearCepikMergeResult';
import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';
import { scrollToInfoEkspert } from 'app/components/NewCarSelectControl/useNewCarSelectControlScroll';
import { useMessages } from 'app/components/NewCarSelectControl/helpers/useMessages';
import { useVehicleType } from 'app/shared/hooks/useVehicleType';
import { useUser } from 'app/shared/hooks/useUser';
import {
  fetchDictionary,
  removeHorsePowerSublabelFromVersionEt,
  removeHorsePowerValueFromVersionEt,
  setHorsePowerDictionary,
} from 'app/components/NewCarSelectControl/helpers/fetchDictionary';
import { useCepikFormRowContainer } from 'app/components/CepikFormRowContainer/useCepikFormRowContainer';
import { NewCarSelectControlProps } from './NewCarSelectControl.interface';
import { useCepikCarData } from './useCepikCarData';

const shouldHideInfoEkspert =
  process.env.REACT_APP_HIDE_INFO_EKSPERT === 'true';

const infoEkspertQuestions = [
  { key: FieldsEnum.CarModelIe, inputIcon: Cog },
  {
    key: FieldsEnum.CarVersionIe,
    inputIcon: Cog,
    option: CarModelDetailsSelectOption,
  },
];

const carDetailsQuestions = [
  { key: FieldsEnum.CarProductionYear, inputIcon: CalendarBlankOutline },
  { key: FieldsEnum.CarMake, inputIcon: Car, option: CarMakeSelectOption },
  { key: FieldsEnum.CarModel, inputIcon: Car },
  { key: FieldsEnum.CarFuelType, inputIcon: GasStation },
  { key: FieldsEnum.CarEngineCapacity, inputIcon: Engine },
  { key: FieldsEnum.CarDoorsCount, inputIcon: CarDoor },
  { key: FieldsEnum.CarHorsePower, inputIcon: Speedometer },
  {
    key: FieldsEnum.CarVersionEt,
    inputIcon: Cog,
    option: CarModelDetailsSelectOption,
  },
  ...(shouldHideInfoEkspert ? [] : infoEkspertQuestions),
];

const motorDetailsQuestions = [
  { key: FieldsEnum.CarProductionYear, inputIcon: CalendarBlankOutline },
  {
    key: FieldsEnum.CarMake,
    inputIcon: Motorbike,
    option: CarMakeSelectOption,
  },
  { key: FieldsEnum.CarModel, inputIcon: Motorbike },
  { key: FieldsEnum.CarFuelType, inputIcon: GasStation },
  { key: FieldsEnum.CarEngineCapacity, inputIcon: Engine },
  { key: FieldsEnum.CarHorsePower, inputIcon: Speedometer },
  {
    key: FieldsEnum.CarVersionEt,
    inputIcon: Cog,
    option: CarModelDetailsSelectOption,
  },
  ...(shouldHideInfoEkspert || isMotorInfoEkspertDisable
    ? []
    : infoEkspertQuestions),
];

export function useNewCarSelect({
  formName,
  cepikModalControl,
  externalTouchedLocally,
}: NewCarSelectControlProps) {
  const isMubiLeasingConfig = useSelector(selectIsMubiLeasingConfig);
  const { isMotor } = useVehicleType();
  const { isOperator } = useUser();
  const quotation = useSelector(selectQuotation);
  const isRenewal = useSelector(selectIsRenewal);
  const [groupExpanded, setGroupExpanded] = useState(false);
  const [hideHorsePowerOptions, setHideHorsePowerOptions] = useState(false);

  let vehicleDetailsQuestions = isMotor
    ? motorDetailsQuestions
    : carDetailsQuestions;

  if (isMotor && formName === FormsEnum.TerminationGeneratorForm) {
    vehicleDetailsQuestions = vehicleDetailsQuestions.filter(
      (q) =>
        ![
          FieldsEnum.CarModelIe,
          FieldsEnum.CarVersionIe,
          FieldsEnum.CarHorsePower,
        ].includes(q.key)
    );
  }

  const {
    activeQuestion,
    activeQuestionIdx,
    markNextQuestionAsActive,
    markQuestionAsActiveByIdx,
    questionsCount,
  } = useActiveQuestion(vehicleDetailsQuestions);

  const { hasAnyMergeResult } = useCepikFormRowContainer(activeQuestion.key);

  const {
    dictionary,
    paramValues,
    question,
    setDictionary,
    patchQuotation,
    changeValue,
    clearDictionaries,
    runBusinessFunctions,
  } = useField(formName, activeQuestion.key);

  const {
    cepikCarData,
    cepikQuestion,
    getCepikDictionary,
    openCepikDataRejectionModal,
  } = useCepikCarData(question, formName);

  const questionKeys = vehicleDetailsQuestions.map((question) => question.key);

  const dispatch = useDispatch();

  const setupFields = () => {
    questionKeys.forEach((key) => {
      dispatch(registerField(formName, key, 'Field'));
    });
  };

  const setupValidators = () => {
    questionKeys.forEach((key) => {
      dispatch(
        FormsConfigActions.setupValidatorForType({
          formName,
          questionKey: key,
        })
      );
    });
  };

  const [everyFieldInitialized, setEveryFieldInitialized] = useState<boolean>(
    false
  );
  const [value, setValue] = useState<Dictionary>([]);
  const [valueBackup, setValueBackup] = useState<Dictionary>([]);
  const [open, setOpen] = useState(false);
  const [wasOpenEverBefore, setWasOpenEverBefore] = useState(false);
  const [
    popperAnchorNode,
    setPopperAnchorNode,
  ] = useState<HTMLDivElement | null>(null);
  const [
    showFillPreviousQuestionsMessage,
    setShowFillPreviousQuestionsMessage,
  ] = useState(false);
  const noOptions = !dictionary || dictionary.length === 0;
  const loading = open && noOptions;
  const isLastValueSelected = value.length === questionsCount;
  const invalidLocally = everyFieldInitialized && !isLastValueSelected;

  const selectedFieldValue = useSelector((state: RootState) =>
    formValueSelector(formName)(state, question.key)
  );

  const carVersionEtFieldValue = useSelector((state: RootState) =>
    formValueSelector(formName)(state, FieldsEnum.CarVersionEt)
  );

  const horsePowerFieldValue = useSelector((state: RootState) =>
    formValueSelector(formName)(state, FieldsEnum.CarHorsePower)
  );

  const dependsOn = question?.dependsOn;
  const dependsOnFieldValue = useSelector((state: RootState) =>
    formValueSelector(formName)(state, dependsOn ? dependsOn[0] : '')
  );

  const carVersionEtFound =
    carVersionEtFieldValue && carVersionEtFieldValue !== 'no_car_type';

  const getInfoekspertFallback = async (answersDictionaryKey: string) => {
    const updatedParamValues = set(
      paramValues,
      FieldsEnum.CarVersionEt,
      'no_car_type'
    );

    return await fetchDictionary(answersDictionaryKey, updatedParamValues);
  };

  const setVersionEtDictionary = (dictionary: Dictionary) => {
    const versionEtDictionary = dictionary
      .filter(
        (item) =>
          item.carTypeDetails?.powerKw === horsePowerFieldValue ||
          item.key === 'no_car_type'
      )
      .map((item) =>
        !hideHorsePowerOptions
          ? {
              ...item,
              value: removeHorsePowerValueFromVersionEt(item),
              ...(item.subLabels && {
                subLabels: removeHorsePowerSublabelFromVersionEt(item),
              }),
            }
          : item
      );

    return versionEtDictionary;
  };

  const getDictionary = async (answersDictionaryKey: string) => {
    let dictionary = await fetchDictionary(answersDictionaryKey, paramValues);

    let isInfoekspertFallback = false;

    const isInfoekspertDictionary = [
      DictionariesEnum.InfoekspertModels,
      DictionariesEnum.InfoekspertModelVersions,
    ].includes(answersDictionaryKey as DictionariesEnum);

    const isVersionEtDictionary = [
      DictionariesEnum.CarModelVersions,
      DictionariesEnum.MotorcycleModelVersions,
    ].includes(answersDictionaryKey as DictionariesEnum);

    if (isInfoekspertDictionary) {
      const carModelOrVersionIeNotFound =
        dictionary.length === 1 && dictionary[0].key.toString() === '-1';

      if (carVersionEtFound && carModelOrVersionIeNotFound) {
        dictionary = await getInfoekspertFallback(answersDictionaryKey);
        isInfoekspertFallback = true;
      }
    }

    let cepikDictionary = getCepikDictionary(activeQuestion.key, dictionary);
    let selectedPowerKw;

    if (isVersionEtDictionary) {
      if (question.key === FieldsEnum.CarHorsePower) {
        const selectedVersionEt = dictionary.find(
          (item) => item.key === carVersionEtFieldValue
        );

        const powerKwFromCepik =
          cepikCarData?.powerKw.length === 1 && cepikCarData?.powerKw[0].key;

        selectedPowerKw =
          powerKwFromCepik || selectedVersionEt?.carTypeDetails?.powerKw;

        dictionary = setHorsePowerDictionary(dictionary);

        setHideHorsePowerOptions(
          dictionary.length === 1 ||
            cepikDictionary.length === 1 ||
            (carVersionEtFieldValue === 'no_car_type' && !horsePowerFieldValue)
        );
      } else if (horsePowerFieldValue) {
        dictionary = setVersionEtDictionary(dictionary);

        if (cepikDictionary.length > 0) {
          cepikDictionary = setVersionEtDictionary(cepikDictionary);
        }
      }
    }

    return {
      cepikDictionary,
      dictionary,
      isInfoekspertFallback,
      selectedPowerKw,
    };
  };

  const initQuestions = () => {
    let active = true;

    const isFieldCleared =
      activeQuestionIdx === 0 && value.length === 0 && valueBackup.length === 0;

    if (isFieldCleared) {
      clearAllDictionaries();
    }

    if (everyFieldInitialized) {
      runBusinessFunctions();
      return;
    }

    (async () => {
      if (!question.answersDictionaryKey) return;

      const {
        cepikDictionary,
        dictionary,
        isInfoekspertFallback,
        selectedPowerKw,
      } = await getDictionary(question.answersDictionaryKey);

      if (!active) return;

      const isHorsePower = question.key === FieldsEnum.CarHorsePower;
      const isEtVersion = activeQuestion.key === FieldsEnum.CarVersionEt;

      const selectedDictionaryEntry = dictionary?.find((entry) =>
        isHorsePower
          ? entry.key === selectedPowerKw
          : entry.key === selectedFieldValue
      );

      setDictionary(
        (cepikModalControl || isEtVersion) && cepikDictionary.length > 0
          ? cepikDictionary
          : dictionary
      );

      const isNoCarType = carVersionEtFieldValue === 'no_car_type';

      if (isHorsePower && carVersionEtFieldValue) {
        if (isNoCarType) {
          setValue((oldValue) => [
            ...oldValue,
            { key: 'no_car_type', value: 'no_car_type' },
          ]);

          runBusinessFunctions();
          markNextQuestionAsActive();
          return;
        }
      }

      if (selectedDictionaryEntry) {
        if (isHorsePower && carVersionEtFieldValue) {
          changeValue(selectedDictionaryEntry.key);
        }

        setValue((oldValue) => [...oldValue, selectedDictionaryEntry]);

        runBusinessFunctions();
        markNextQuestionAsActive();
      } else {
        if (!isFieldCleared) {
          setTouchedLocally(true);
          unsetOptionsNextToActive(activeQuestionIdx);
        }

        autofillFieldValue(
          cepikDictionary.length > 0 ? cepikDictionary : dictionary,
          isInfoekspertFallback
        );

        if (cepikDictionary.length !== 1) {
          setEveryFieldInitialized(true);
        }

        if (activeQuestionIdx > 0 && cepikDictionary.length > 1) {
          setOpen(true);
        }
      }
    })();

    return () => {
      active = false;
    };
  };

  const initFields = () => {
    setupFields();
    setupValidators();
  };

  const setOption = (option: DictionaryEntry, dictionary?: Dictionary) => {
    setValue((oldValue) => {
      if (
        oldValue.length === questionsCount &&
        activeQuestionIdx === questionsCount - 1
      ) {
        return getNewArrWithLastItemReplaced(oldValue, option);
      } else {
        return [...oldValue.slice(0, activeQuestionIdx), option];
      }
    });

    changeValue(option.key);
    dispatch(
      FormsConfigActions.validateField({
        formName,
        questionKey: question.key as FieldsEnum,
        value: option.key,
      })
    );
    patchQuotation(option.key);
    runBusinessFunctions();

    if (
      activeQuestion.key === FieldsEnum.CarHorsePower &&
      dictionary?.length === 1
    ) {
      markQuestionAsActiveByIdx(activeQuestionIdx + 1);
    } else {
      markNextQuestionAsActive();
    }
  };

  const autofillFieldValue = (
    dictionary: Dictionary,
    isInfoekspertFallback: boolean
  ) => {
    if (value.length > activeQuestionIdx) {
      return;
    }

    if (cepikModalControl) {
      const cepikValues = dictionary.filter((e) => e.group === 'cepik');
      const formValues = dictionary.filter((e) => e.group === 'cepik-form');

      if (
        formValues.length &&
        cepikValues.length === 1 &&
        cepikValues[0].key === formValues[0].key
      ) {
        setOption(formValues[0]);
        return;
      }
    }

    if (dictionary.length === 1) {
      const [onlyAvailableOption] = dictionary;
      setOption(onlyAvailableOption, dictionary);
      return;
    }

    const isICanNotFindMyCar = (dictEntry: DictionaryEntry) =>
      [
        FieldsEnum.CarVersionEt,
        FieldsEnum.CarModelIe,
        FieldsEnum.CarVersionIe,
      ].includes(activeQuestion.key) &&
      ['-1', 'no_car_type'].includes(dictEntry.key.toString());

    if (dictionary.length === 2 && dictionary.some(isICanNotFindMyCar)) {
      const onlyAvailableOption = dictionary.find(
        (dictEntry) => !isICanNotFindMyCar(dictEntry)
      );

      if (
        activeQuestion.key === FieldsEnum.CarVersionEt &&
        isInfoekspertFallback
      ) {
        return;
      }

      if (onlyAvailableOption) {
        setOption(onlyAvailableOption);
        return;
      }
    }

    const previousValue = valueBackup[activeQuestionIdx];

    if (previousValue) {
      const previousOption = dictionary.find(
        (dictEntry) => dictEntry.key === previousValue.key
      );
      if (previousOption && !isICanNotFindMyCar(previousOption)) {
        setOption(previousOption);
        return;
      }
    }

    const defaultOption = dictionary?.find((dictEntry) => dictEntry.selected);

    if (defaultOption) {
      setOption(defaultOption);
      return;
    }
  };

  const unsetOptionsNextToActive = (index: number) => {
    setValue((oldValue) => [...oldValue.slice(0, index)]);
    markQuestionAsActiveByIdx(index);

    clearDictionaries(questionKeys.slice(index + 1));
    if (formName !== FormsEnum.CompareCepikDataForm)
      clearFieldsValue(questionKeys.slice(index), formName);
    runBusinessFunctions();
  };

  const unsetOptions = (removedOption: DictionaryEntry) => {
    const removedOptionIndex = findIndex(value, removedOption);
    const noHorsePowerValueProvided =
      removedOption.key === 'no_car_type' && !horsePowerFieldValue;

    if (removedOptionIndex === 0) {
      setValueBackup(value);
    } else {
      if (noHorsePowerValueProvided) {
        setValueBackup(value.slice(0, removedOptionIndex - 2));
      } else {
        setValueBackup(value.slice(0, removedOptionIndex - 1));
      }
    }

    if (noHorsePowerValueProvided) {
      setValue((oldValue) => [...oldValue.slice(0, removedOptionIndex - 1)]);
      markQuestionAsActiveByIdx(removedOptionIndex - 1);
      clearDictionaries(questionKeys.slice(removedOptionIndex));
    } else {
      setValue((oldValue) => [...oldValue.slice(0, removedOptionIndex)]);
      markQuestionAsActiveByIdx(removedOptionIndex);
      clearDictionaries(questionKeys.slice(removedOptionIndex + 1));
    }

    if (formName !== FormsEnum.CompareCepikDataForm) {
      clearFieldsValue(questionKeys.slice(removedOptionIndex), formName);
    }

    runBusinessFunctions();
  };

  const clearAllDictionaries = () => {
    clearDictionaries(questionKeys);
  };

  const onLoadingChange = () => {
    let active = true;

    if (!loading) {
      return;
    }

    (async () => {
      if (!question.answersDictionaryKey) {
        return;
      }

      const {
        cepikDictionary,
        dictionary,
        isInfoekspertFallback,
      } = await getDictionary(question.answersDictionaryKey);

      if (active) {
        if (activeQuestion.key === FieldsEnum.CarVersionEt) {
          setDictionary(
            cepikDictionary.length > 0 ? cepikDictionary : dictionary
          );
        } else {
          setDictionary(
            cepikModalControl && cepikDictionary.length > 0
              ? cepikDictionary
              : dictionary
          );
        }

        autofillFieldValue(
          cepikDictionary.length > 0 ? cepikDictionary : dictionary,
          isInfoekspertFallback
        );
      }
    })();

    return () => {
      active = false;
    };
  };

  const canScrollToInfoExpert = (currentValue: Dictionary) =>
    isMobileOrTablet() && currentValue.length === 7;

  const shouldFocusNextQuestion = () => {
    const insuranceStartDateInput = document.querySelector<HTMLElement>(
      'input[name="insurance.startDate"]'
    );
    const isInsuranceStartDateInputInvalid =
      insuranceStartDateInput?.getAttribute('aria-invalid') === 'true';
    return (
      isLastValueSelected &&
      activeQuestionIdx === questionsCount - 1 &&
      (open || isInsuranceStartDateInputInvalid)
    );
  };

  const onValueChange = () => {
    if (
      wasOpenEverBefore &&
      isOperator &&
      hasAnyMergeResult &&
      formName !== FormsEnum.CompareCepikDataForm &&
      [
        FieldsEnum.CarMake,
        FieldsEnum.CarModel,
        FieldsEnum.CarFuelType,
      ].includes(activeQuestion.key)
    ) {
      dispatch(clearCepikMergeResult(quotation.id));
    }

    if (canScrollToInfoExpert(value)) {
      scrollToInfoEkspert();
    }

    if (shouldFocusNextQuestion()) {
      setOpen(false);
    }

    if (!everyFieldInitialized && isLastValueSelected) {
      setEveryFieldInitialized(true);
    }
  };

  const onOpenChange = () => {
    if (open) {
      return;
    }

    (document?.activeElement as HTMLElement)?.blur();

    /**
     * This is needed for scenario when user select tag (onTagClick)
     * which is not the last one, closes dropdown (onBlur) without selecting value
     * and opens dropdown again (onOpen)
     */
    markQuestionAsActiveByIdx(value.length);
  };

  const onChange: StepSelectControlProps['onChange'] = (
    event,
    val,
    reason,
    details
  ) => {
    if (details === undefined) {
      return;
    }

    if (reason === 'select-option') {
      const selectedOption = details.option;
      const valueChanged =
        selectedOption?.key !== value[activeQuestionIdx]?.key;

      if (
        valueChanged &&
        [
          FieldsEnum.CarProductionYear,
          FieldsEnum.CarMake,
          FieldsEnum.CarModel,
        ].includes(activeQuestion.key)
      ) {
        clearVin();

        if (isRenewal) {
          clearFieldsValue([FieldsEnum.CarRegistrationNumber]);
          obfuscation.showQuotationRegistrationNumberObfuscatedMessage();
          obfuscation.showQuotationCarVinObfuscatedMessage();
        }
      }

      if (activeQuestionIdx < value.length) {
        if (activeQuestionIdx === 0) {
          setValueBackup(value);
        } else {
          setValueBackup(valueBackup.slice(0, activeQuestionIdx - 1));
        }
      }

      if (!valueChanged && everyFieldInitialized) {
        setOpen(false);
      } else {
        setOpen(true);
        setOption(selectedOption);
      }
    }

    if (reason === 'remove-option') {
      const removedOption = details.option;
      const removedOptionIndex = findIndex(value, removedOption);

      unsetOptions(removedOption);

      if (isMubiLeasingConfig && removedOptionIndex === 0) {
        clearFieldValue(FieldsEnum.CarFirstRegistrationDate, formName, false);
      }

      setOpen(true);
      document
        .querySelector<HTMLInputElement>(
          'div[aria-label="car autocomplete"] input'
        )
        ?.focus();
    }
  };

  const onTagClick: (option: DictionaryEntry) => void = (
    elem: DictionaryEntry
  ) => {
    setOpen(true);
    const selectedOptionIndex = findIndex(value, elem);
    if (activeQuestionIdx === selectedOptionIndex) {
      return;
    }
    clearDictionaries(questionKeys.slice(selectedOptionIndex));
    markQuestionAsActiveByIdx(selectedOptionIndex);
  };

  const onTagFocus: (option: DictionaryEntry) => void = (elem) => {
    setOpen(true);
    const selectedOptionIndex = findIndex(value, elem);
    if (activeQuestionIdx === selectedOptionIndex) {
      return;
    }
    markQuestionAsActiveByIdx(selectedOptionIndex);
  };

  const options: StepSelectControlProps['options'] = dictionary
    ? question.answerGroups && !cepikModalControl
      ? getGroupedOptions(question.answerGroups, dictionary)
      : dictionary
    : [];

  const groupBy: StepSelectControlProps['groupBy'] =
    cepikQuestion.answerGroups || question.answerGroups
      ? groupByOptionGroup
      : undefined;

  const onOpen: StepSelectControlProps['onOpen'] = () => setOpen(true);

  const { setTouchedLocally, error, messages, message } = useMessages(
    questionKeys,
    invalidLocally,
    formName
  );

  const onBlur: StepSelectControlProps['onBlur'] = () => {
    if (activeQuestionIdx === value.length) {
      unsetOptionsNextToActive(activeQuestionIdx);
    }

    setOpen(false);
    setTouchedLocally(true);
  };

  const popperAnchorRef = useCallback((node) => {
    setPopperAnchorNode(node);
  }, []);

  const getCepikGroupLabel = (questionKey: string, group: 'cepik' | 'form') => {
    return {
      [FieldsEnum.CarProductionYear]: {
        cepik: `${LabelEnum.ProductionYear.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${LabelEnum.ProductionYear.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarMake]: {
        cepik: `${LabelEnum.Make.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${LabelEnum.Make.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarModel]: {
        cepik: `${ControlLabelEnum.Model.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${ControlLabelEnum.Model.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarFuelType]: {
        cepik: `${LabelEnum.FuelType.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${LabelEnum.FuelType.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarEngineCapacity]: {
        cepik: `${LabelEnum.EngineCapacity.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${LabelEnum.EngineCapacity.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarDoorsCount]: {
        cepik: `${LabelEnum.DoorsCount.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${LabelEnum.DoorsCount.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarHorsePower]: {
        cepik: `${LabelEnum.CarHorsePower.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${LabelEnum.CarHorsePower.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarVersionEt]: {
        cepik: `${CompareCepikDataEnum.Version.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${CompareCepikDataEnum.Version.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarModelIe]: {
        cepik: `${ControlLabelEnum.ModelIe.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${ControlLabelEnum.ModelIe.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
      [FieldsEnum.CarVersionIe]: {
        cepik: `${CompareCepikDataEnum.Version.toUpperCase()} ${
          CompareCepikDataEnum.FromCepik
        }`,
        form: `${CompareCepikDataEnum.Version.toUpperCase()} ${
          CompareCepikDataEnum.FromForm
        }`,
      },
    }[questionKey]![group];
  };

  const sortCepikVersions = (
    versions: Array<Exclude<React.ReactNode, boolean | null | undefined>>
  ) =>
    versions.sort((a: ReactElement, b: ReactElement) => {
      const valueA =
        a.props.children.props?.valueShort || a.props.children.props?.value;
      const valueB =
        b.props.children.props?.valueShort || b.props.children.props?.value;
      return valueA > valueB ? 1 : -1;
    });

  useEffect(() => {
    if (!wasOpenEverBefore && open) setWasOpenEverBefore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (externalTouchedLocally) {
      setTouchedLocally(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalTouchedLocally]);

  useEffect(() => {
    setShowFillPreviousQuestionsMessage(
      !!dependsOn && !UtilService.hasValue(dependsOnFieldValue)
    );
  }, [dependsOn, dependsOnFieldValue]);

  useEffect(() => {
    if (
      activeQuestion.key === FieldsEnum.CarHorsePower &&
      dictionary &&
      dictionary.length > 0
    ) {
      setHideHorsePowerOptions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuestion]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(initQuestions, [activeQuestion]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onLoadingChange, [loading]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onValueChange, [value]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onOpenChange, [open]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(initFields, []);

  return {
    activeQuestion,
    error,
    everyFieldInitialized,
    groupBy,
    groupExpanded,
    isLastValueSelected,
    loading,
    message,
    messages,
    onBlur,
    onChange,
    onOpen,
    onTagClick,
    onTagFocus,
    open,
    options,
    question,
    value,
    invalidLocally,
    popperAnchorRef,
    popperAnchorNode,
    showFillPreviousQuestionsMessage,
    hideHorsePowerOptions,
    setGroupExpanded,
    getCepikGroupLabel,
    sortCepikVersions,
    openCepikDataRejectionModal,
  };
}
